import { useEffect, useState } from 'react';
import type { ArtManifest } from '@/game/assets/manifest';

/**
 * The art manifest as the DOM UI sees it (item thumbnails, dialogue portraits, photo stickers). Fetched once;
 * null while there is no art yet, so every caller keeps its placeholder.
 */
export const ART_BASE = './art/';

let manifest: Promise<ArtManifest | null> | undefined;
/** The manifest once fetched, so screens opened later draw their art on the first render (no fallback flash). */
let loaded: ArtManifest | null | undefined;

export function loadArtManifest(): Promise<ArtManifest | null> {
  manifest ??= fetch(`${ART_BASE}manifest.json`)
    .then((res) => (res.ok ? (res.json() as Promise<ArtManifest>) : null))
    .catch(() => null)
    .then((m) => (loaded = m));
  return manifest;
}

const urlOf = (m: ArtManifest | null | undefined, name: string) => {
  const file = m?.ui[name];
  return file ? `${ART_BASE}${file}` : null;
};

/** URL of a standalone UI image (por_*, stk_*, avt_*), or null when it has no art yet. */
export async function uiImageUrl(name: string): Promise<string | null> {
  return urlOf(await loadArtManifest(), name);
}

export function useUiImage(name: string | null | undefined): string | null {
  const [found, setFound] = useState<{ name: string; url: string | null } | null>(null);
  useEffect(() => {
    if (!name) return;
    let live = true;
    void uiImageUrl(name).then((url) => {
      if (live) setFound({ name, url });
    });
    return () => {
      live = false;
    };
  }, [name]);
  if (!name) return null;
  if (found?.name === name) return found.url;
  // Manifest already fetched: answer at once instead of showing the fallback for a frame.
  return loaded !== undefined ? urlOf(loaded, name) : null;
}
